import { FC } from "react";

import { FormType, getField, getFormBackgroundColor, getOrganizationPageNewsletterUrl } from "@simplyk/common";
import { useLocaleContext } from "../../../../common/contexts/LocaleContext";
import { ZeffyHead } from "../ZeffyHead";

import { buildEventsJsonLd } from "./helpers/buildEventsJsonLd";
import { getMissionPlainText } from "./helpers/getMissionPlainText";
import { useOrganizationPageMetaContent } from "./useOrganizationPageMetaContent";

import { OrganizationPageOutput } from "@/common/types/trpc";

interface OrganizationPageHeadProps {
  organizationPage: OrganizationPageOutput;
}

export const OrganizationPageHead: FC<OrganizationPageHeadProps> = ({ organizationPage }) => {
  const { locale } = useLocaleContext();
  const field = getField(organizationPage.organizationPageFields, locale);
  const title = organizationPage.organization?.name;
  const missionText = getMissionPlainText(field?.description);
  const themeColor = getFormBackgroundColor(organizationPage.color, Boolean(organizationPage.isDarkMode));

  const { metaTitle, metaDescription } = useOrganizationPageMetaContent({
    organizationPage,
  });

  const defaultDonationCampaign = organizationPage.defaultDonationCampaign;
  const defaultDonationAction = defaultDonationCampaign
    ? {
        "@type": "DonateAction" as const,
        name: getField(defaultDonationCampaign.fields, locale)?.title,
        url: defaultDonationCampaign.url,
      }
    : undefined;

  const supportForms = organizationPage.supportForms?.map((form) => {
    const formField = getField(form.fields, locale);
    return {
      "@type": form.formType === FormType.DonationForm ? ("DonateAction" as const) : ("BuyAction" as const),
      name: formField?.title,
      description: formField?.plainTextDescription,
      url: form.url,
    };
  });

  const newsletters = organizationPage.isNewslettersSectionEnabled
    ? organizationPage.newsletters.map((newsletter) => ({
        "@type": "BlogPosting" as const,
        headline: newsletter.subject,
        url: getOrganizationPageNewsletterUrl(organizationPage.url, newsletter.id),
        ...(newsletter.sentAt && { datePublished: newsletter.sentAt }),
      }))
    : [];

  const allActions = [...(defaultDonationAction ? [defaultDonationAction] : []), ...(supportForms || [])];
  const sameAs = organizationPage.socialLinks.map((socialLink) => socialLink.url);
  const footerAddress = organizationPage.footerAddress;
  const websiteUrl = organizationPage.isWebsiteShown ? organizationPage.organization?.website : null;
  const events = buildEventsJsonLd({
    upcomingEvents: organizationPage.upcomingEvents,
    locale,
    organizer: { name: organizationPage.organization?.name, url: websiteUrl || organizationPage.url },
  });

  return (
    <ZeffyHead
      title={title}
      description={missionText || undefined}
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      themeColor={themeColor}
      image={organizationPage.bannerUrl || organizationPage.organization?.logoUrl || undefined}
      imageFit={organizationPage.bannerUrl ? "cover" : "contain"}
      noIndex={Boolean(organizationPage.shouldNotBeIndexed)}
    >
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "WebPage",
          "@id": organizationPage.url,
          name: title,
          description: missionText,
          url: organizationPage.url,
          ...(organizationPage.bannerUrl && { image: organizationPage.bannerUrl }),
          mainEntity: {
            "@type": "NGO",
            name: organizationPage.organization?.name,
            ...(websiteUrl ? { url: websiteUrl } : {}),
            ...(organizationPage.organization?.logoUrl ? { logo: organizationPage.organization.logoUrl } : {}),
            ...(sameAs.length > 0 ? { sameAs } : {}),
            ...(footerAddress
              ? {
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: footerAddress.address,
                    addressLocality: footerAddress.city,
                    ...(footerAddress.region ? { addressRegion: footerAddress.region } : {}),
                    ...(footerAddress.postalCode ? { postalCode: footerAddress.postalCode } : {}),
                    addressCountry: footerAddress.countryCode,
                  },
                }
              : {}),
            ...(footerAddress && footerAddress.latitude !== null && footerAddress.longitude !== null
              ? {
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: footerAddress.latitude,
                    longitude: footerAddress.longitude,
                  },
                }
              : {}),
            ...(events.length > 0 ? { event: events } : {}),
            ...(allActions.length > 0 ? { potentialAction: allActions } : {}),
            ...(newsletters.length > 0 ? { subjectOf: newsletters } : {}),
          },
        })}
      </script>
    </ZeffyHead>
  );
};
